import type { JSX } from 'preact';
import { useEffect, useMemo } from 'preact/hooks';
import { MemoryRouter, Route, Routes } from 'react-router-dom';
import { createApiClient, setApiClient } from '@/lib/api-client.js';
import { MarketplaceFlow } from '@/flows/marketplace/marketplace-flow.js';
import { OutreachFlow } from '@/flows/outreach/outreach-flow.js';
import { RedirectSuccess } from '@/flows/marketplace/screens/redirect-success.js';
import { ErrorScreen } from '@/flows/marketplace/screens/error.js';
import { WidgetError } from '@/components/widget-error.js';
import { WidgetLoading } from '@/components/widget-loading.js';
import { WidgetErrorBoundary } from '@/components/error-boundary.js';
import { FullscreenLayer } from '@/flows/outreach/components/fullscreen-layer.js';
import { ChatError } from '@/flows/outreach/components/chat-error.js';
import { OUTREACH_FATAL_MESSAGE, bootErrorCopy } from '@/lib/error-copy.js';
import { DEFAULT_BRAND, config, configState, loadConfig } from '@/state/config.js';

interface AppProps {
  apiKey: string;
  apiBase: string;
  /** The outreach route, when the host page knows it and the fragment doesn't carry it. */
  outreachId: string | null;
  /** False when nothing sits behind the widget to dismiss to — see `OutreachFlow`. */
  dismissible: boolean;
}

export function App({ apiKey, apiBase, outreachId, dismissible }: AppProps) {
  // Child useEffects fire before the parent's, so we must set the api-client
  // singleton during render — useMemo guarantees this runs once before
  // children mount.
  const client = useMemo(() => {
    const c = createApiClient({ apiBase, apiKey });
    setApiClient(c);
    return c;
  }, [apiKey, apiBase]);

  useEffect(() => {
    void loadConfig(client).catch(() => {});
  }, [client]);

  // The dealer's fragment is read once and never written or listened to. A
  // `HashRouter` would make it shared state: their `<a href="#contact">` would
  // reroute us to a path no `<Route>` matches and blank the widget, and every step
  // we took would rewrite their address bar. Nothing here navigates, so seeding a
  // `MemoryRouter` is the whole routing story.
  //
  // Only our own two entry points are honoured, rather than any `#/…`, because the
  // fragment belongs to the dealer: plenty of dealer sites hash-route themselves,
  // and `#/inventory` would otherwise match no `<Route>` and render nothing.
  //
  // The `outreach-id` attribute is the same entry by another road, for our hosted page — a
  // server route, where a fragment never arrives. The fragment must still outrank it: Budco
  // returns to `…/offer/<id>#/success` with the attribute still set, and letting the attribute
  // win would drop the customer back into the chat instead of the receipt.
  const initialRoute = useMemo(() => {
    const hash = window.location.hash;
    if (/^#\/(offer\/|success)/.test(hash)) return hash.slice(1);
    return outreachId ? `/offer/${outreachId}` : '/';
  }, [outreachId]);

  // Applied once, here, so every subtree inherits it — both flows, the loading state,
  // and the boot/fatal error cards, which render inside neither flow's shell.
  const brandColor = config.value?.brandColor ?? DEFAULT_BRAND;
  const withBrand = (children: JSX.Element) => (
    // `.sm-root` is `display: contents` — it carries the custom property without
    // participating in layout, so `.sm-fullscreen`'s `position: fixed` still
    // resolves against the viewport.
    <div className="sm-root" style={{ '--sm-brand': brandColor } as JSX.CSSProperties}>
      {children}
    </div>
  );

  // Gate the flows on config: a config failure means we can't reach the backend
  // or the key is bad, so surface it up front rather than letting each screen
  // fail piecemeal. This is the app layer's boot-failure branch.
  const boot = configState.value;
  if (boot.kind === 'loading') {
    // Neutral: `brandColor` is null until the call this is waiting on returns, so a branded arc
    // here would be the fallback rather than the dealer's colour.
    return withBrand(<WidgetLoading neutral />);
  }

  if (boot.kind === 'error') {
    const copy = bootErrorCopy(boot.errorKind);
    const retry = () => void loadConfig(client).catch(() => {});
    const onRetry = copy.retryable ? retry : undefined;
    // Config gates both flows before the router mounts, so a boot failure lands
    // here for whichever link the customer opened. Keep an outreach link in the
    // chat paradigm rather than dropping them onto the generic card.
    if (initialRoute.startsWith('/offer/')) {
      return withBrand(
        <FullscreenLayer>
          <ChatError message={copy.message} onRetry={onRetry} />
        </FullscreenLayer>,
      );
    }
    return withBrand(
      <div className="sm-boot">
        <WidgetError title={copy.title} message={copy.message} onRetry={onRetry} />
      </div>,
    );
  }

  // Three error layers: the app boundary is the fatal safety net around the whole
  // router; each flow route gets its own boundary with a domain-styled fallback so
  // an unexpected throw is owned by (and looks like) its own flow.
  return withBrand(
    <WidgetErrorBoundary
      fallback={(reset) => (
        <div className="sm-boot">
          <WidgetError
            title="Something went wrong"
            message="The widget hit an unexpected error."
            onRetry={reset}
          />
        </div>
      )}
    >
      <MemoryRouter initialEntries={[initialRoute]}>
        <Routes>
          <Route
            path="/"
            element={
              <WidgetErrorBoundary fallback={(reset) => <ErrorScreen onReset={reset} />}>
                <MarketplaceFlow />
              </WidgetErrorBoundary>
            }
          />
          <Route
            path="/offer/:outreachId"
            element={
              <WidgetErrorBoundary
                fallback={(reset) => (
                  <FullscreenLayer>
                    <ChatError message={OUTREACH_FATAL_MESSAGE} onRetry={reset} />
                  </FullscreenLayer>
                )}
              >
                <OutreachFlow dismissible={dismissible} />
              </WidgetErrorBoundary>
            }
          />
          <Route path="/success" element={<RedirectSuccess />} />
        </Routes>
      </MemoryRouter>
    </WidgetErrorBoundary>,
  );
}
